import { Quote } from "../types/Quote.interface";
import { SegmentUserIdentifier, BrowserExtraData, BuilderUserIdentifier } from "@spotpetinsurance/spot-types/misc/BrowserExtraData";
import { IdentifyTraitsTransforms } from "@spotpetinsurance/spot-types/vendor/enhanced-commerce/IdentifyTraitsTransforms";
import { QuoteDataUtils } from "./QuoteDataUtils";
import { StorageUtils } from "./StorageUtils";
import { UIUtils } from "./UIUtils";
import { getGlobalStatsigClient } from "../contexts/BootstrappedStatsigProvider";
import { PublicConfig } from "../PublicConfig";
import { CookieUtils } from "./CookieUtils";

type SegmentEcommerceEvents =
    | "Product Clicked"
    | "Product Viewed"
    | "Product Added"
    | "Product Removed"
    | "Cart Viewed"
    | "Checkout Started"
    | "Checkout Step Viewed"
    | "Checkout Step Completed"
    | "Payment Info Entered"
    | "Order Completed"
    | "Order Updated"
    | "Order Refunded"
    | "Order Cancelled";

type SegmentOtherEvents =
    | "Continue Clicked"
    | "Pet Added"
    | "Pet Removed"
    | "Quote Retrieved"
    | "Quote Found"
    | "Account Already Exists"
    | "Update Billing Error - Setup"
    | "Update Billing Error - Finalize"
    | "Update Billing Error - Unknown"
    | "Login Error - URL Generation"
    | "Login Error - Code Validation"
    | "ApplePay - MPAN Recurring Payment Created"
    | "Thank You - Plan PDF Clicked"
    | "Thank You - App Download CTA Clicked"
    | "Thank You - Member Center - Account Created"
    | "Thank You - Member Center - Vet - Added"
    | "Thank You - Member Center - Vet - MC Link Clicked"
    | "Thank You - Member Center - Direct Deposit - Created"
    | "Thank You - Member Center - Skip to MC Link Clicked"
    | "Plan ID Override"
    | "Annual Limit Migration"
    | "Deductible Migration"
    | "Reimbursement Migration"
    | "Coverage Migration";

export type SegmentEvents = SegmentEcommerceEvents | SegmentOtherEvents;

export type EventProperties = {
    [x: string]: any;
};

type SpotSession = {
    browserSessionId?: string;
    testUserId?: string;
    variantId?: string;
};

type StatsigLayerData = {
    ruleID: string;
    allocatedExperimentName: string;
};

type MultiLayerStatsigExposureData = {
    statsig: {
        userID: string;
        [layerId: string]: StatsigLayerData | string;
    };
};

export type AllBrowserExtraData = BrowserExtraData & SpotSession & MultiLayerStatsigExposureData;

export class AnalyticsUtils {
    private static segmentIdRetryPromise: Promise<string> | null = null;
    private static readonly MAX_RETRIES = 5;
    private static readonly RETRY_DELAY = 1000;

    static getSegmentIds(email?: string): SegmentUserIdentifier | undefined {
        if (typeof window === "undefined") {
            return undefined;
        }

        const anonymousId = CookieUtils.get("ajs_anonymous_id");
        const userId = CookieUtils.get("ajs_user_id");

        return {
            userId: userId ?? (email ? window.btoa(email) : ""),
            anonymousId: anonymousId ?? ""
        };
    }

    /**
     * Sets the DataDog RUM user with marketing attributes and optional identify payload
     * Reads from both spotWebDataDogUser cookie AND query params (for iframe scenarios)
     */
    static setDataDogUser(userId: string, identifyPayload?: any, spotWebAttributes?: any) {
        if (typeof window === "undefined" || !window.DD_RUM) {
            return;
        }

        // Try to get spotWebAttributes from multiple sources
        let attributes = spotWebAttributes;

        if (!attributes) {
            // First try: Read from cookie
            try {
                const spotWebDataDogUserStr = CookieUtils.get("spotWebDataDogUser");
                if (spotWebDataDogUserStr) {
                    const spotWebDataDogUser = JSON.parse(spotWebDataDogUserStr);
                    if (typeof spotWebDataDogUser === "object" && spotWebDataDogUser?.spotWebAttributes) {
                        attributes = spotWebDataDogUser.spotWebAttributes;
                    }
                }
            } catch (error) {
                console.warn("Error parsing spotWebDataDogUser cookie:", error);
            }

            // Second try: Read Builder test data from query params (fallback for iframe)
            if (!attributes || !attributes.builder) {
                const builderFromQuery = AnalyticsUtils.getBuilderDataFromQueryParams();
                if (builderFromQuery && Object.keys(builderFromQuery.tests).length > 0) {
                    attributes = {
                        ...attributes,
                        builder: builderFromQuery
                    };
                }
            }
        }

        window.DD_RUM.onReady(() => {
            window.DD_RUM?.setUser({
                id: userId,
                ...(attributes && { spotWebAttributes: attributes }),
                ...identifyPayload
            });
        });
    }

    /**
     * Extracts Builder.io test data from query parameters
     * Query params format: builderVariant.{testId}={variantId}
     * Returns in same format as BuilderUserIdentifier for consistency
     */
    static getBuilderDataFromQueryParams(): { sessionId: string; tests: Record<string, string> } | null {
        if (typeof window === "undefined") return null;

        const searchParams = new URLSearchParams(window.location.search);
        const tests: Record<string, string> = {};
        let sessionId = "";

        // Extract builderSessionId
        const sessionIdParam = UIUtils.getCaseInsensitiveValue(searchParams, "builderSessionId");
        if (sessionIdParam) {
            sessionId = sessionIdParam;
        }

        // Extract all builderVariant.* params
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith("builderVariant.")) {
                const testId = key.replace("builderVariant.", "");
                tests[testId] = value;
            }
        }

        // Only return if we have either sessionId or tests
        if (sessionId || Object.keys(tests).length > 0) {
            return { sessionId, tests };
        }

        return null;
    }

    static async identifyUser(quote: Quote) {
        if (typeof window === "undefined" || !quote.email) {
            return;
        }

        const currentCookieUserId = CookieUtils.get("ajs_user_id");
        const newUserId = window.btoa(quote.email);

        // Try to get stored DataDog user attributes from the cookie
        let spotWebAttributes = undefined;

        try {
            const spotWebDataDogUserStr = CookieUtils.get("spotWebDataDogUser");
            if (spotWebDataDogUserStr) {
                const spotWebDataDogUser = JSON.parse(spotWebDataDogUserStr);
                if (typeof spotWebDataDogUser === "object" && spotWebDataDogUser?.spotWebAttributes) {
                    spotWebAttributes = spotWebDataDogUser.spotWebAttributes;
                }
            }
        } catch (error) {
            console.warn("Error parsing spotWebDataDogUser cookie:", error);
        }

        // Only perform identity change if we have different IDs
        if (currentCookieUserId && currentCookieUserId !== newUserId) {
            try {
                const decodedCurrentUserId = window.atob(currentCookieUserId);

                if (decodedCurrentUserId !== quote.email && window.analytics) {
                    // First, identify with the new ID
                    const identifyPayload = IdentifyTraitsTransforms.petQuoteToIdentifyTraits(QuoteDataUtils.quoteToPetQuote(quote));

                    // Wait for identify to complete
                    await new Promise<void>(resolve => {
                        window.analytics?.identify(newUserId, identifyPayload, () => {
                            window.analytics?.alias(newUserId, currentCookieUserId);
                            resolve();
                        });
                    });

                    // Update DataDog if available
                    if (!!window.DD_RUM) {
                        window.DD_RUM.onReady(() => {
                            // Merge the identify payload with marketing attributes from the cookie
                            window.DD_RUM?.setUser({
                                id: newUserId,
                                spotWebAttributes,
                                ...identifyPayload
                            });
                        });
                    }
                }
            } catch (error) {
                console.error("Error in identity change process:", error);
            }
        } else {
            // Simple identify if no ID change needed
            const identifyPayload = IdentifyTraitsTransforms.petQuoteToIdentifyTraits(QuoteDataUtils.quoteToPetQuote(quote));
            window.analytics?.identify(newUserId, identifyPayload);
            AnalyticsUtils.setDataDogUser(newUserId, identifyPayload);
        }
    }

    static trackSegmentEvent(event: SegmentEvents, properties?: EventProperties, callback?: () => void) {
        try {
            window.analytics?.track(event, properties, callback);
        } catch (error) {
            console.error(`Error tracking Segment event: `, event);
        }

        if (window.DD_RUM) {
            try {
                window.DD_RUM.onReady(() => {
                    window.DD_RUM?.addAction(event, properties);
                });
            } catch (error) {
                console.error(`Error tracking Datadog event: `, event);
            }
        }
    }

    static buildSegmentLocationObject() {
        if (typeof window === "undefined" || typeof document === "undefined") return {};

        const { location }: { location: Location } = window;

        const title = document?.title || `Start Your Free Quote | Spot Pet Insurance`;
        const referrer = document?.referrer || undefined;

        const { pathname = ``, search = ``, href = `` } = location || {};

        const locationObj = {
            path: pathname,
            referrer,
            search,
            title,
            url: href
        };

        return locationObj;
    }

    static initSegmentPageCall(attempts = 0) {
        if (!!window.analytics?.page) {
            window.analytics?.page(undefined, undefined, AnalyticsUtils.buildSegmentLocationObject());
            return;
        }

        if (attempts > 0) {
            setTimeout(() => AnalyticsUtils.initSegmentPageCall(attempts - 1), 500);
        }

        if (attempts === 0) {
            console.warn(`Unable to locate analytics.js`);
        }
    }

    static getQueryParams = (quoteParams?: Record<string, string>) => {
        const currentParams = typeof window !== "undefined" ? Object.fromEntries(new URLSearchParams(window.location.search)) : {};
        const mergedParams = { ...quoteParams, ...currentParams };

        return mergedParams;
    };

    static getBuilderSessionId(): string {
        const currentSessionId = CookieUtils.get("builderSessionId") ?? "";
        return currentSessionId;
    }

    static getBuilderTests(): BuilderUserIdentifier["tests"] {
        const allCookies = CookieUtils.getAll();
        const filteredCookies = Object.entries(allCookies).filter(([name, value]) => name.includes("builder.tests."));

        const tests: { [testId: string]: string } = {};
        filteredCookies.forEach(([name, value]) => {
            const testId = name.replace("builder.tests.", "");
            tests[testId] = value;
        });

        return tests;
    }

    static getBuilderObject(): BuilderUserIdentifier {
        return {
            sessionId: AnalyticsUtils.getBuilderSessionId(),
            tests: AnalyticsUtils.getBuilderTests()
        };
    }

    static getSpotSessionObject(testUserId?: string): SpotSession {
        const queryParams = new URLSearchParams(window.location.search);
        const browserSessionId = StorageUtils.getItem("browserSessionId") ?? undefined;
        const updatedTestUserId = testUserId ?? UIUtils.getCaseInsensitiveValue(queryParams, "tuid") ?? CookieUtils.get("tuid");

        return {
            browserSessionId,
            testUserId: updatedTestUserId
        };
    }

    static getUserAgent(): string {
        return typeof window !== "undefined" ? window.navigator.userAgent : "";
    }

    static getStatsigExposureData(): MultiLayerStatsigExposureData | undefined {
        const statsigClient = getGlobalStatsigClient();
        if (!statsigClient) {
            return;
        }

        const testUserId = statsigClient.getContext().user?.userID ?? "";

        const result: MultiLayerStatsigExposureData = {
            statsig: {
                userID: testUserId
            }
        };

        for (const layerId of PublicConfig.ALL_STATSIG_LAYERS) {
            const testLayer = statsigClient.getLayer(layerId);
            const allocatedExperimentName = testLayer?.__evaluation?.allocated_experiment_name;

            if (allocatedExperimentName) {
                result.statsig[layerId] = {
                    ruleID: testLayer?.ruleID ?? "",
                    allocatedExperimentName: allocatedExperimentName
                };
            }
        }

        return result;
    }

    static sendDataDogAction(actionName: string, actionProperties?: Record<string, any>) {
        if (typeof window !== "undefined" && window.DD_RUM) {
            window.DD_RUM.onReady(() => {
                window.DD_RUM?.addAction(actionName, actionProperties);
            });
        }
    }

    static sendDataDogLog(logLevel: "debug" | "info" | "warn" | "error" = "info", message: string, logProperties?: Record<string, any>) {
        if (typeof window !== "undefined" && window.DD_LOGS) {
            window.DD_LOGS.onReady(() => {
                window.DD_LOGS?.logger[logLevel](message, logProperties);
            });
        }
    }

    static setDataDogViewName(viewName: string) {
        if (typeof window !== "undefined" && window.DD_RUM) {
            window.DD_RUM.onReady(() => {
                window.DD_RUM?.setViewName(viewName);
            });
        }
    }

    static startDataDogView(viewName: string) {
        if (typeof window !== "undefined" && window.DD_RUM) {
            window.DD_RUM.onReady(() => {
                window.DD_RUM?.startView({ name: viewName });
            });
        }
    }

    static constructExtra(quoteParams?: Record<string, string>, email?: string, testUserId?: string): AllBrowserExtraData {
        const statsigData = AnalyticsUtils.getStatsigExposureData();

        return {
            queryParams: AnalyticsUtils.getQueryParams(quoteParams),
            userAgent: AnalyticsUtils.getUserAgent(),
            segment: AnalyticsUtils.getSegmentIds(email),
            builder: {
                ...AnalyticsUtils.getBuilderObject()
            },
            ...AnalyticsUtils.getSpotSessionObject(testUserId),
            statsig: statsigData?.statsig ?? {
                userID: ""
            }
        };
    }

    /**
     * Gets a user ID for DataDog with fallback chain and retry logic
     * Priority: Segment anonymous ID > DataDog session ID > Browser session ID
     * Includes retry logic to handle async Segment script loading
     */
    static async getDataDogFallbackUserId(): Promise<string> {
        // Reuse existing promise if already polling
        if (this.segmentIdRetryPromise) {
            return this.segmentIdRetryPromise;
        }

        this.segmentIdRetryPromise = new Promise<string>(resolve => {
            const attemptGet = (retryCount = 0) => {
                // Try Segment anonymous ID first
                const segmentAnonymousId = CookieUtils.get("ajs_anonymous_id");
                if (segmentAnonymousId) {
                    resolve(segmentAnonymousId);
                    this.segmentIdRetryPromise = null;
                    return;
                }

                // If we haven't hit max retries, try again
                if (retryCount < this.MAX_RETRIES) {
                    setTimeout(() => attemptGet(retryCount + 1), this.RETRY_DELAY);
                    return;
                }

                // Max retries reached - use fallbacks
                console.warn("AnalyticsUtils: Segment ID not found after max retries, using fallback");

                // Try DataDog's own ID
                if (typeof window !== "undefined" && window.DD_RUM) {
                    try {
                        const ddContext = window.DD_RUM.getInternalContext();
                        if (ddContext?.session_id) {
                            resolve(`dd_${ddContext.session_id}`);
                            this.segmentIdRetryPromise = null;
                            return;
                        }
                    } catch (error) {
                        console.warn("Could not get DataDog session ID:", error);
                    }
                }

                // Last resort: browser session ID
                const browserSessionId = StorageUtils.getItem("browserSessionId");
                if (browserSessionId) {
                    resolve(`browser_${browserSessionId}`);
                } else {
                    resolve(`anonymous_${Date.now()}`);
                }

                this.segmentIdRetryPromise = null;
            };

            attemptGet();
        });

        return this.segmentIdRetryPromise;
    }
}
